import _, { get } from 'lodash';
import { computed, makeObservable, observable, observe } from 'mobx';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import { Component } from 'react';
import styled from 'styled-components';

import { Button, Card, Col, Row, Table, Tooltip, Upload } from 'antd';

import { showDemoVinOrRealVin } from 'shared/utils';

import {
  AppStore,
  CarStore,
  ContactStore,
  CurrentUserStore,
  ShopStore,
} from 'stores';
import { CarObject, Logger } from 'stores/Classes';

import { interpolateColors } from 'helpers/color-generator';
import {
  calcMileage,
  calcMileageBack,
  convertHourToSeconds,
} from 'helpers/unitCalculations';

import DriverSummary from './DriverSummary';

import {
  DeleteOutlined,
  EditOutlined,
  FilePdfOutlined,
  PlusCircleOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import WarrantiesTable from 'components/Warranties/WarrantiesTable';
import moment from 'moment-timezone';
import { customFleets } from 'shared';
import { TextField } from 'shared/PitstopUI';
import { DoughnutChart } from 'shared/PitstopUI/PitstopChart';
import WarrantyStore from 'stores/WarrantyStore';
import CustomFileds from './CustomFields';

const TextRow = styled.div`
  display: flex;
  margin-top: 4px;
  margin-bottom: 4px;
  height: 34px;
`;

const LabelText = styled.p`
  width: 40%;
  font-weight: 700;
  margin-bottom: 0;
  align-self: center;
`;

const ContentText = styled.p`
  flex: 1;
  margin-bottom: 0;
  align-self: center;
`;

const SectionTitle = styled.h3`
  font-size: 22px;
  font-weight: 400;
  padding-left: 12px;
  margin-top: 16px;
`;

const CardInfo = styled.p`
  font-size: 14px;
  font-weight: 400;
  padding: 0;
`;

const SmallTable = styled(Table)`
  .ant-table-small {
    border: none;
  }

  .ant-table-small
    > .ant-table-content
    > .ant-table-body
    > table
    > .ant-table-thead
    > tr
    > th {
    font-weight: 600;
  }

  button {
    color: #595959;
  }
`;

const UploadedDocuments = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-top: 10px;
  max-height: 200px;
  overflow: auto;
`;

const UploadDocumentRow = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 10px;
  border: 1px solid #f0f0f0;
  margin-bottom: 10px;

  .document-info {
    margin-left: 10px;
    flex: 1;

    .file-title {
      margin-bottom: 0;
      font-weight: 600;
      font-size: 13px;
    }

    .added-on {
      margin: 2px 0 0;
      font-size: 12px;
      color: #595959;
    }
  }

  .ant-btn {
    color: #595959;
  }
`;

const UploadedDocument = ({ title, addedOn }) => {
  return (
    <UploadDocumentRow>
      <FilePdfOutlined style={{ fontSize: '24px' }} />
      <div className="document-info">
        <p className="file-title">{title}</p>
        <p className="added-on">Added on {addedOn}</p>
      </div>
      <Button type="link" icon={<DeleteOutlined />} />
    </UploadDocumentRow>
  );
};

UploadedDocument.propTypes = {
  title: PropTypes.string.isRequired,
  addedOn: PropTypes.string.isRequired,
};

class CarProfileHeader extends Component {
  static propTypes = {
    car: PropTypes.instanceOf(CarObject),
    shopId: PropTypes.number.isRequired,
  };

  observableCar = null;
  observableShopId = null;

  disposer = observe(CarStore, 'demo', async () => {
    if (CarStore.demo) {
      await this.getVehicleDetailReport();
      await this.getVehicleGeneralReport();
    }
  });

  state = {
    warrantyData: [],
    isLoadingAsssetWarranties: true,
    warrantyFilters: {},
    warrantyPagination: {},
    warrantySorter: {},
    vendors: [],
  };

  constructor(props) {
    super(props);

    this.observableCar = observable.box(props.car);
    this.observableShopId = observable.box(props.shopId);

    makeObservable(this, {
      shopName: computed,
      mainDriverName: computed,
      vehicleDetailReport: computed,
      vehicleGeneralReport: computed,
      breakdownReasonsData: computed,
      issuesData: computed,
      integrationStatus: computed,
      observableCar: observable,
      observableShopId: observable,
    });
  }

  async componentDidMount() {
    this.observableCar.set(this.props.car);
    this.observableShopId.set(this.props.shopId);

    await this.getVehicleDetailReport();
    await this.getVehicleGeneralReport();

    if (!this.integrationStatus.loaded) {
      try {
        await this.props.car.getIntegrationStatus();
      } catch (error) {
        Logger.error(error);
      }
    }

    await this.loadShopVendors();
    await this.loadWarranties();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.car !== this.props.car) {
      this.observableCar.set(this.props.car);
    }

    if (prevProps.shopId !== this.props.shopId) {
      this.observableShopId.set(this.props.shopId);
    }
  }

  componentWillUnmount() {
    this.disposer();
  }

  loadShopVendors = async () => {
    try {
      const shopId = this.observableShopId.get();

      if (!shopId || shopId === '-1' || shopId === -1) {
        this.setState({ vendors: [] });
        return;
      }

      const vendors = await ContactStore.getShopContacts(shopId);
      this.setState({ vendors });
    } catch (error) {
      Logger.error(error);
    }
  };

  loadWarranties = async () => {
    try {
      this.setState({ isLoadingAsssetWarranties: true });

      const car = this.observableCar.get();
      const { id } = car;

      const filters = {
        ...this.state.warrantyFilters,
        carIds: id,
      };

      if (filters.status && filters.status.length) {
        filters.statuses = filters.status.join(',');
      }

      if (this.state.warrantyPagination.current) {
        const page = this.state.warrantyPagination.current;
        filters.limit = this.state.warrantyPagination.pageSize;
        filters.offset = (page - 1) * filters.limit;
      }

      if (this.state.warrantySorter.column && this.state.warrantySorter.order) {
        const field = this.state.warrantySorter.field;
        const direction =
          this.state.warrantySorter.order === 'ascend' ? '' : '-';
        filters.sortBy = `${direction}${field}`;
      }

      const shopId = this.observableShopId.get();
      const response = await WarrantyStore.getWarranties(shopId, filters);

      const { data = [], meta = {} } = response || {};

      this.setState({
        warrantyData: data,
        warrantyPagination: {
          ...this.state.warrantyPagination,
          total: meta?.total || 0,
        },
      });
    } catch (error) {
      Logger.error(error);
    } finally {
      this.setState({ isLoadingAsssetWarranties: false });
    }
  };

  getVehicleDetailReport = async () => {
    const car = this.observableCar.get();
    if (car) {
      await car.getVehicleDetailReport();
    }
  };

  getVehicleGeneralReport = async () => {
    const car = this.observableCar.get();
    if (car) {
      await car.getVehicleGeneralReport();
    }
  };

  get vehicleDetailReport() {
    const car = this.observableCar.get();
    return car ? car.detailReport : {};
  }

  get vehicleGeneralReport() {
    const car = this.observableCar.get();
    return car ? car.generalReport : {};
  }

  get mileageField() {
    const car = this.observableCar.get();
    if (!car) return null;

    const totalMileage = calcMileage(Number(car.totalMileage));

    return (
      <TextRow>
        <LabelText>Mileage</LabelText>
        <TextField
          placeholder="Enter new mileage"
          showToast={true}
          type="float"
          defaultValue={totalMileage.toFixed(1)}
          units={CurrentUserStore.user.displayedOdometer || 'miles'}
          onSubmit={(value) =>
            CarStore.updateCar(car, {
              totalMileage: Number(calcMileageBack(value)),
            })
          }
        />
      </TextRow>
    );
  }

  get carNameField() {
    const car = this.observableCar.get();
    if (!car) return null;

    const { carName } = car;
    /**
     *  const currentCarMeta = car.meta || {};
            currentCarMeta.mainDriverName = value;
            await CarStore.updateCar(car, { meta: currentCarMeta });
            AppStore.addSuccess('Main driver updated successfully');
     *
     */
    return (
      <TextRow>
        <LabelText>Unit Id</LabelText>
        <TextField
          showToast={true}
          placeholder="Enter an unique identifier for the vehicle"
          defaultValue={carName}
          onSubmit={(value) => CarStore.updateCar(car, { carName: value })}
        />
      </TextRow>
    );
  }

  get carVinField() {
    const car = this.observableCar.get();
    if (!car) return null;

    const { vin } = car;

    return (
      <TextRow>
        <LabelText>VIN</LabelText>
        <TextField
          showToast={true}
          placeholder="Enter new VIN"
          defaultValue={showDemoVinOrRealVin(vin)}
          onSubmit={(value) => CarStore.updateCar(car, { vin: value })}
        />
      </TextRow>
    );
  }

  get deviceField() {
    const car = this.observableCar.get();
    if (!car) return null;

    const { scanner } = car;
    const device = scanner ? scanner.scannerId || scanner.id : null;

    return (
      <TextRow>
        <LabelText>Device</LabelText>
        <TextField
          placeholder="Enter new device id"
          noValueText="No Device Connected"
          showToast={true}
          defaultValue={device}
          onSubmit={(value) =>
            CarStore.updateScannerForCar(car, { scannerId: value })
          }
        />
      </TextRow>
    );
  }

  get carMake() {
    const car = this.observableCar.get();
    if (!car) return null;

    const { make } = car;

    return (
      <TextRow>
        <LabelText>Make</LabelText>
        <TextField
          showToast={true}
          placeholder="Enter car make"
          defaultValue={make}
          onSubmit={(value) => CarStore.updateCar(car, { make: value })}
        />
      </TextRow>
    );
  }

  get mainDriverField() {
    const car = this.observableCar.get();
    if (!car) return null;

    const { meta } = car;
    const mainDriver = this.mainDriverName;

    return (
      <TextRow>
        <LabelText>Main Driver</LabelText>
        <TextField
          showToast={false}
          placeholder="Enter main driver"
          noValueText="N/A"
          defaultValue={mainDriver}
          onSubmit={async (value) => {
            const currentCarMeta = car.meta || {};
            currentCarMeta.mainDriverName = value;
            await CarStore.updateCar(car, { meta: currentCarMeta });
            AppStore.addSuccess('Main driver updated successfully');
          }}
        />
      </TextRow>
    );
  }

  get carModel() {
    const car = this.observableCar.get();
    if (!car) return null;

    const { model } = car;

    return (
      <TextRow>
        <LabelText>Model</LabelText>
        <TextField
          showToast={true}
          placeholder="Enter car model"
          defaultValue={model}
          onSubmit={(value) => CarStore.updateCar(car, { model: value })}
        />
      </TextRow>
    );
  }

  get carYear() {
    const car = this.observableCar.get();
    if (!car) return null;

    const { year } = car;

    return (
      <TextRow>
        <LabelText>Year</LabelText>
        <TextField
          showToast={true}
          placeholder="Enter car year"
          defaultValue={year}
          type="year"
          maxLength={4}
          onSubmit={(value) => CarStore.updateCar(car, { year: value })}
        />
      </TextRow>
    );
  }

  get carEngineHours() {
    const car = this.observableCar.get();
    if (!car) return null;

    return (
      <TextRow>
        <LabelText>Engine Hours</LabelText>
        <TextField
          showToast={true}
          placeholder="Enter car engine hours"
          type="float"
          defaultValue={Math.round(car.getEngineHours()).toReadable()}
          onSubmit={async (value) => {
            let engineHours = convertHourToSeconds(value);

            if (!_.isNil(engineHours)) {
              await CarStore.updateCar(car, {
                engineHours,
              });
            }
          }}
        />
      </TextRow>
    );
  }

  get issuesData() {
    if (CarStore.demo === false) {
      return {
        key: 'issues',
        value: [],
      };
    }

    const car = this.observableCar.get();
    if (!car) return { key: 'issues', value: [] };

    const { issueCounts } = car;
    let value = [
      {
        name: 'critical',
        color: '#eb3d34',
        y: Number(issueCounts.critical) || 0,
      },
      {
        name: 'major',
        color: '#e28931',
        y: Number(issueCounts.major) || 0,
      },
      {
        name: 'minor',
        color: '#e2eb34',
        y: Number(issueCounts.minor) || 0,
      },
    ];

    return {
      key: '',
      seriesName: 'issues',
      emptyMessage: 'Your vehicle has no Issues',
      value,
    };
  }

  get tagsField() {
    const car = this.observableCar.get();
    if (!car) return null;

    const { tags } = car;

    return (
      <TextRow>
        <LabelText>Tags</LabelText>
        <ContentText>
          {tags && tags.replace ? tags.replace(/"/g, '') : 'No tags available'}
        </ContentText>
      </TextRow>
    );
  }

  get breakdownReasonsData() {
    if (CarStore.demo === false) {
      return {
        key: 'breakdowns',
        value: [],
      };
    }

    let vehicleDetail = this.vehicleDetailReport;

    let breakdownReasons = _.get(vehicleDetail, 'data.reasonBreakdown');

    let labels = [],
      value = [];

    if (!breakdownReasons) {
      if (_.isBoolean(CarStore.demo)) {
        value = [
          { name: 'Road Call', y: 1 },
          { name: 'Warranty', y: 20 },
          { name: 'Driver Report', y: 24 },
          { name: 'Routine', y: 4 },
          { name: 'PM', y: 7 },
          { name: 'Inspection', y: 4 },
          { name: 'Capitalization', y: 1 },
        ];
      }
    } else {
      labels = Object.keys(breakdownReasons);

      value = _.map(labels, (label) => ({
        name: label,
        y: breakdownReasons[label] || 0,
      }));
    }

    let colors = interpolateColors(labels.length);

    value = _.map(value, (v, i) => ({
      ...v,
      color: colors[i],
    }));

    return {
      seriesName: 'downtime',
      emptyMessage: 'Your vehicle has no downtime',
      value,
    };
  }

  get shopName() {
    const shopId = this.observableShopId.get();
    if (!shopId) return 'N/A';

    if (!ShopStore.data.has(shopId)) return 'N/A';

    return ShopStore.data.get(shopId).name;
  }

  get mainDriverName() {
    const car = this.observableCar.get();
    if (!car) return null;

    return car?.meta?.mainDriverName || null;
  }

  get integrationStatus() {
    const car = this.observableCar.get();
    return car ? car.integrationStatus : {};
  }

  renderChart = (data) => {
    return (
      <DoughnutChart
        chart={{
          height: 200,
          margin: [0, 200, 0, 0],
          legend: {
            align: 'right',
            layout: 'vertical',
            verticalAlign: 'top',
            width: 150,
            x: -30,
            y: 30,
          },
        }}
        data={data}
        loaded={CarStore.demo === false || this.vehicleDetailReport.loaded}
      />
    );
  };

  render() {
    const car = this.observableCar.get();
    const shopId = this.observableShopId.get();

    if (!car) {
      return <div>No car data available</div>;
    }

    const integrationStatus = this.integrationStatus;
    const faultCodeMigrationSuccessEvent = _.find(integrationStatus.data, (i) =>
      _.get(i, 'event.id', '').includes('geotab_dtc_pull|success')
    );

    const faultCodeMigrationSuccessEventTimestamp = moment(
      _.get(faultCodeMigrationSuccessEvent, 'meta.triggeredAt', NaN)
    );

    const getFaultCodeMigrationText = () =>
      `${faultCodeMigrationSuccessEventTimestamp.format(
        'LLLL'
      )} (${faultCodeMigrationSuccessEventTimestamp.fromNow()})`;

    const isPfgFleet = customFleets.pfgFleet.includes(
      ShopStore.currentShop.parentShopId || ShopStore.currentShop.id
    );
    const isUfpFleet = customFleets.ufpFleet.includes(
      ShopStore.currentShop.parentShopId || ShopStore.currentShop.id
    );

    return (
      <>
        <Row gutter={[12, 32]}>
          <Col span={8}>
            <SectionTitle>Asset Details</SectionTitle>
            <Card>
              {this.carNameField}
              {this.carVinField}
              {this.mileageField}
              {this.deviceField}
              <TextRow>
                <LabelText>Fleet</LabelText>
                <ContentText>{this.shopName}</ContentText>
              </TextRow>
              {/* <TextRow>
                <LabelText>Main Driver</LabelText>
                <ContentText>{this.mainDriverName}</ContentText>
              </TextRow> */}
              {this.mainDriverField}
              {this.carMake}
              {this.carModel}
              {this.carYear}
              {this.carEngineHours}
              {this.tagsField}
              <TextRow
                style={{
                  display: faultCodeMigrationSuccessEvent
                    ? 'inline-table'
                    : 'none',
                }}
              >
                <LabelText>Fault Code Last Checked At</LabelText>
                <ContentText>{getFaultCodeMigrationText()}</ContentText>
              </TextRow>
              <TextRow>
                <LabelText>Tire Specs:</LabelText>
                <TextField
                  placeholder="Enter tire specs"
                  defaultValue={get(car, 'meta.tireSpecs', '')}
                  onSubmit={async (value) => {
                    await CarStore.updateCar(car, {
                      meta: {
                        ...get(car, 'meta', {}),
                        tireSpecs: value,
                      },
                    });

                    AppStore.addSuccess('Your vehicle has been updated!');
                  }}
                />
              </TextRow>
              <TextRow>
                <LabelText>Tire Size</LabelText>
                <TextField
                  placeholder="Enter tire size"
                  defaultValue={get(car, 'meta.tireSize', '')}
                  onSubmit={async (value) => {
                    await CarStore.updateCar(car, {
                      meta: {
                        ...get(car, 'meta', {}),
                        tireSize: value,
                      },
                    });
                  }}
                />
              </TextRow>
              <TextRow>
                <LabelText>Brand</LabelText>
                <TextField
                  placeholder="Enter brand"
                  defaultValue={get(car, 'meta.tireBrand', '')}
                  onSubmit={async (value) => {
                    await CarStore.updateCar(car, {
                      meta: {
                        ...get(car, 'meta', {}),
                        tireBrand: value,
                      },
                    });
                  }}
                />
              </TextRow>
              <TextRow>
                <LabelText>Notes</LabelText>
                <TextField
                  placeholder="Enter notes"
                  defaultValue={get(car, 'meta.notes', '')}
                  onSubmit={async (value) => {
                    await CarStore.updateCar(car, {
                      meta: {
                        ...get(car, 'meta', {}),
                        notes: value,
                      },
                    });
                  }}
                />
              </TextRow>
              <TextRow>
                <LabelText>Vendor</LabelText>
                <TextField
                  placeholder="Enter vendor name"
                  defaultValue={get(car, 'meta.vendor', '')}
                  onSubmit={async (value) => {
                    await CarStore.updateCar(car, {
                      meta: {
                        ...get(car, 'meta', {}),
                        vendor: value,
                      },
                    });
                  }}
                />
              </TextRow>
              <TextRow>
                <LabelText>Tire Location</LabelText>
                <TextField
                  placeholder="Enter tire location"
                  defaultValue={get(car, 'meta.tireLocation', '')}
                  onSubmit={async (value) => {
                    await CarStore.updateCar(car, {
                      meta: {
                        ...get(car, 'meta', {}),
                        tireLocation: value,
                      },
                    });
                  }}
                />
              </TextRow>
              <TextRow>
                <LabelText>
                  {isPfgFleet ? 'License Plate' : 'Additional Information'}
                </LabelText>
                <TextField
                  placeholder={
                    isPfgFleet
                      ? 'Enter license plate'
                      : 'Enter additional information'
                  }
                  defaultValue={get(car, 'meta.additionalInfo', '')}
                  onSubmit={async (value) => {
                    await CarStore.updateCar(car, {
                      meta: {
                        ...get(car, 'meta', {}),
                        additionalInfo: value,
                      },
                    });
                  }}
                />
              </TextRow>
              <CustomFileds carId={car.id} customFileds={car.details} />
            </Card>
          </Col>

          <Col span={16}>
            <SectionTitle>Warranties</SectionTitle>
            <Card>
              <CardInfo>
                Enter any warranties associated with the vehicle that are
                relevant to keep record
              </CardInfo>
              <WarrantiesTable
                data={this.state.warrantyData}
                isLoading={this.state.isLoadingAsssetWarranties}
                assets={[car]}
                preSelectedAssets={[car.id]}
                vendors={this.state.vendors}
                onUpdate={this.loadWarranties}
                onChangePagination={({ pagination, filters, sorter }) => {
                  this.setState(
                    {
                      warrantyPagination: pagination,
                      warrantyFilters: filters,
                      warrantySorter: sorter,
                    },
                    this.loadWarranties
                  );
                }}
              />
            </Card>
            {/* {ShopStore.currentShop.id === 374 && (
              <>
                <SectionTitle>Purchase History</SectionTitle>
                <Card>
                  <CardInfo>
                    Keep track of any sales or costs associated with the asset
                    that is relevant
                  </CardInfo>
                  <SmallTable
                    size="small"
                    columns={[
                      {
                        title: 'Desc.',
                        dataIndex: 'desc',
                        key: 'desc',
                      },
                      {
                        title: 'Purchased',
                        dataIndex: 'purchaseDate',
                        key: 'purchaseDate',
                      },
                      {
                        title: 'Sold',
                        dataIndex: 'sold',
                        key: 'sold',
                      },
                      {
                        title: '',
                        key: 'action',
                        render: () => (
                          <Tooltip title="Edit">
                            <Button
                              icon={<EditOutlined />}
                              size="sm"
                              type="link"
                            />
                          </Tooltip>
                        ),
                      },
                    ]}
                    dataSource={[
                      {
                        key: '1',
                        desc: `${car?.make || 'Asset'} Purchase`,
                        purchaseDate: '2023-01-03',
                        sold: '2024-01-02',
                      },
                    ]}
                    pagination={false}
                  />
                  <Button
                    type="link"
                    icon={<PlusCircleOutlined />}
                    style={{ marginTop: 10 }}
                    onClick={() => { }}
                  >
                    Add New Row
                  </Button>
                </Card>
              </>
            )} */}
          </Col>
        </Row>
        {/* {ShopStore.currentShop.id === 374 && (
          <Row gutter={[12, 2]}>
            <Col span={16}>
              <SectionTitle>Document Upload</SectionTitle>
              <Card>
                <CardInfo>
                  Upload documents such as DOT inspections, service records,
                  warranty info, etc for your asset
                </CardInfo>
                <Upload name="file" onChange={() => { }}>
                  <Button
                    icon={<UploadOutlined />}
                    style={{ marginTop: 10 }}
                    onClick={() => { }}
                  >
                    Upload
                  </Button>
                </Upload>
                <UploadedDocuments>
                  <UploadedDocument
                    title="DOT inspection.pdf"
                    addedOn="2023-01-03"
                  />
                  <UploadedDocument
                    title="Billofsale.pdf"
                    addedOn="2023-01-03"
                  />
                  <UploadedDocument title="Image478.pdf" addedOn="2023-01-03" />
                  <UploadedDocument title="Invoice.pdf" addedOn="2023-01-03" />
                </UploadedDocuments>
              </Card>
            </Col>
          </Row>
        )} */}
        <div style={{ marginTop: 10 }}>
          <DriverSummary shopId={shopId} />
        </div>
      </>
    );
  }
}

export default observer(CarProfileHeader);
